import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../AuthContext';
import NotificationContext from '../NotificationContext';
import LoadingSpinner from './LoadingSpinner';
import ReviewModal from './ReviewModal'; // Import the new ReviewModal component

const MyBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useContext(AuthContext);
    const { showNotification } = useContext(NotificationContext);

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);

    useEffect(() => {
        const fetchBookings = async () => {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Please log in to view your bookings.');
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get('https://local-technician-finder-backend.onrender.com/api/bookings/my', {
                    headers: {
                        'x-auth-token': token
                    }
                });
                setBookings(res.data);
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.msg || 'Failed to fetch bookings.');
            } finally {
                setLoading(false);
            }
        };
        fetchBookings();
    }, [showNotification]);

    const updateBookingStatus = async (bookingId, newStatus) => {
        const token = localStorage.getItem('token');
        if (!token) {
            showNotification('Please log in to update booking status.', 'error');
            return;
        }

        try {
            await axios.put(`https://local-technician-finder-backend.onrender.com/api/bookings/${bookingId}/status`, { status: newStatus }, {
                headers: {
                    'x-auth-token': token
                }
            });
            showNotification(`Booking status updated to ${newStatus}!`, 'success');
            // Refresh bookings list
            const res = await axios.get('https://local-technician-finder-backend.onrender.com/api/bookings/my', {
                headers: {
                    'x-auth-token': token
                }
            });
            setBookings(res.data);
        } catch (err) {
            console.error(err.response.data);
            showNotification(err.response.data.msg || 'Failed to update booking status.', 'error');
        }
    };

    const handleOpenReviewModal = (booking) => {
        setSelectedBookingForReview(booking);
        setShowReviewModal(true);
    };

    const handleCloseReviewModal = () => {
        setShowReviewModal(false);
        setSelectedBookingForReview(null);
    };

    const handleReviewSuccess = (reviewedBookingId) => {
        // Update the status of the reviewed booking in the current state
        setBookings(prevBookings => 
            prevBookings.map(booking => 
                booking._id === reviewedBookingId ? { ...booking, hasReview: true } : booking
            )
        );
    };

    if (loading) return <LoadingSpinner />;
    if (error) return <div>{error}</div>;

    return (
        <div className="container mt-4">
            <h1 className="mb-4 text-center">My Bookings</h1>
            {bookings.length === 0 ? (
                <p className="text-center">No bookings found.</p>
            ) : (
                <div className="row row-cols-1 row-cols-md-2 g-2">
                    {bookings.map(booking => {
                        console.log(`Rendering booking ID: ${booking._id}, Status: ${booking.status}, hasReviewed: ${booking.hasReviewed}`);
                        return (
                        <div className="col" key={booking._id}>
                            <div className="card h-100 shadow-sm">
                                <div className="card-body d-flex flex-column p-2">
                                    <h5 className="card-title mb-1">Service: {booking.serviceId.name}</h5>
                                    {user && user.role === 'customer' ? (
                                        <p className="card-text mb-1"><strong>Technician:</strong> {booking.technicianId.username}</p>
                                    ) : (
                                        <>
                                            <p className="card-text mb-1"><strong>Customer:</strong> {booking.customerId.username}</p>
                                            <p className="card-text mb-1"><strong>Customer Email:</strong> {booking.customerId.email}</p>
                                            {booking.customerId.address && <p className="card-text mb-1"><strong>Customer Address:</strong> {booking.customerId.address}</p>}
                                        </>
                                    )}
                                    <p className="card-text mb-1"><strong>Scheduled:</strong> {new Date(booking.scheduledDateTime).toLocaleString()}</p>
                                    <p className="card-text mb-1"><strong>Status:</strong> <span className={`badge bg-${booking.status === 'pending' ? 'warning' : booking.status === 'confirmed' ? 'info' : booking.status === 'completed' ? 'success' : 'danger'}`}>{booking.status}</span></p>
                                    <p className="card-text flex-grow-1 mb-1"><strong>Notes:</strong> {booking.customerNotes}</p>

                                    <div className="mt-auto d-flex justify-content-between align-items-center">
                                        <div>
                                            {user && user.role === 'technician' && booking.status === 'pending' && (
                                                <>
                                                    <button onClick={() => updateBookingStatus(booking._id, 'confirmed')} className="btn btn-success btn-sm me-2">Confirm</button>
                                                    <button onClick={() => updateBookingStatus(booking._id, 'cancelled')} className="btn btn-danger btn-sm">Cancel</button>
                                                </>
                                            )}
                                            {user && user.role === 'technician' && booking.status === 'confirmed' && (
                                                <>
                                                    <button onClick={() => updateBookingStatus(booking._id, 'completed')} className="btn btn-primary btn-sm me-2">Mark as Completed</button>
                                                    <button onClick={() => updateBookingStatus(booking._id, 'cancelled')} className="btn btn-danger btn-sm">Cancel</button>
                                                </>
                                            )}
                                            {user && user.role === 'customer' && booking.status === 'pending' && (
                                                <button onClick={() => updateBookingStatus(booking._id, 'cancelled')} className="btn btn-danger btn-sm">Cancel</button>
                                            )}
                                        </div>
                                        {user && user.role === 'customer' && booking.status === 'completed' && !booking.hasReviewed && (
                                            <button 
                                                onClick={() => handleOpenReviewModal(booking)} 
                                                className="btn btn-primary btn-sm"
                                            >
                                                Submit Review
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );})}
                </div>
            )}
            {showReviewModal && (
                <ReviewModal 
                    booking={selectedBookingForReview} 
                    onClose={handleCloseReviewModal} 
                    onReviewSuccess={handleReviewSuccess} 
                />
            )}
        </div>
    );
};

export default MyBookings;

    
